import React, { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ShieldCheck, ArrowRight, Sparkles } from "lucide-react";
import { useCatalog } from "../contexts/CatalogContext";

export default function Pricing() {
  const { categories, AMC_PLANS } = useCatalog();
  const [activeCat, setActiveCat] = useState(categories[0]?.id || "");
  const cat = categories.find((c) => c.id === activeCat) || categories[0] || {};

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
      <div className="text-center max-w-2xl mx-auto mb-10">
        <span className="text-xs font-semibold text-[#FF6B2B] uppercase tracking-widest">Pricing</span>
        <h1 className="font-display font-extrabold text-4xl sm:text-5xl text-slate-900 mt-3">Simple, transparent pricing</h1>
        <p className="mt-4 text-slate-600">"Starting from" rates cover labour for the listed scope. Spares billed at MRP after your approval.</p>
      </div>

      {/* No hidden charges banner */}
      <div className="bg-[#22C55E]/10 border border-[#22C55E]/30 rounded-xl p-4 flex items-center justify-center gap-3 mb-10" data-testid="no-hidden-banner">
        <ShieldCheck className="w-5 h-5 text-[#22C55E]" />
        <span className="text-sm font-semibold text-slate-800">No hidden charges. No advance. Pay only after the job is done.</span>
      </div>

      {/* Category tabs */}
      <div className="flex flex-wrap gap-2 justify-center mb-10" data-testid="pricing-tabs">
        {categories.map((c) => (
          <button
            key={c.id}
            data-testid={`pricing-tab-${c.id}`}
            onClick={() => setActiveCat(c.id)}
            className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${
              activeCat === c.id ? "bg-[#1A3C6E] text-white" : "bg-white border border-slate-200 text-slate-700 hover:border-[#1A3C6E]"
            }`}
          >
            {c.short}
          </button>
        ))}
      </div>

      {/* Pricing Table */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden card-shadow">
        <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between" style={{ backgroundColor: cat.bg + "40" }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: cat.bg, color: cat.color }}>
              <cat.icon className="w-5 h-5" />
            </div>
            <h2 className="font-display font-bold text-xl text-slate-900">{cat.name} — Price List</h2>
          </div>
          <span className="hidden sm:inline text-xs font-semibold text-slate-500">{cat.brands.length} brands supported</span>
        </div>
        <table className="w-full" data-testid={`pricing-table-${cat.id}`}>
          <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
            <tr>
              <th className="px-6 py-3">Service</th>
              <th className="px-6 py-3 hidden md:table-cell">Duration</th>
              <th className="px-6 py-3 text-right">Starting at</th>
              <th className="px-6 py-3 text-right hidden sm:table-cell">Action</th>
            </tr>
          </thead>
          <tbody>
            {cat.services.map((s) => (
              <tr key={s.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50">
                <td className="px-6 py-4">
                  <div className="font-semibold text-slate-900">{s.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{s.desc}</div>
                </td>
                <td className="px-6 py-4 hidden md:table-cell text-sm text-slate-600">{s.time}</td>
                <td className="px-6 py-4 text-right">
                  <span className="font-display font-bold text-lg text-[#1A3C6E]">
                    ₹{s.price}
                    {s.id.includes("amc") && <span className="text-xs font-medium text-slate-500">/yr</span>}
                  </span>
                </td>
                <td className="px-6 py-4 text-right hidden sm:table-cell">
                  <Link
                    to={`/booking?category=${cat.id}&service=${s.id}`}
                    data-testid={`pricing-book-${s.id}`}
                    className="text-sm font-semibold text-[#FF6B2B] hover:text-[#E65A20] inline-flex items-center gap-1"
                  >
                    Book <ArrowRight className="w-4 h-4" />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Segment pricing note */}
      <p className="text-xs text-slate-500 mt-4 text-center" data-testid="segment-pricing-note">
        Shop / office rates may differ for bulk units and commercial contracts. Get a custom quote on{" "}
        <Link to="/contact" className="text-[#FF6B2B] font-semibold">Contact</Link>.
      </p>

      {/* AMC Plans */}
      <section className="mt-20" data-testid="amc-plans-section">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <span className="text-xs font-semibold text-[#FF6B2B] uppercase tracking-widest">AMC Plans</span>
          <h2 className="font-display font-bold text-3xl sm:text-4xl text-slate-900 mt-2">Annual Maintenance — Peace of mind, year-round</h2>
          <p className="mt-3 text-slate-600">Applicable to RO, AC and Washing Machine. Per appliance, per year.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {AMC_PLANS.map((p) => (
            <div
              key={p.name}
              data-testid={`amc-plan-${p.name.toLowerCase()}`}
              className={`relative rounded-2xl p-7 border-2 ${
                p.popular ? "border-[#FF6B2B] bg-white shadow-xl" : "border-slate-200 bg-white"
              }`}
            >
              {p.popular && (
                <span className="absolute -top-3 right-6 bg-[#FF6B2B] text-white text-xs font-bold px-3 py-1 rounded-full inline-flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Most popular
                </span>
              )}
              <div className="font-display font-bold text-xl text-slate-900">{p.name}</div>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="font-display font-extrabold text-4xl text-[#1A3C6E]">₹{p.price}</span>
                <span className="text-sm text-slate-500">/year</span>
              </div>
              <ul className="mt-6 space-y-3">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                    <CheckCircle2 className="w-4 h-4 text-[#22C55E] mt-0.5 flex-shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <Link
                to="/booking"
                data-testid={`amc-select-${p.name.toLowerCase()}`}
                className={`mt-7 block text-center font-semibold py-3 rounded-xl transition-all ${
                  p.popular
                    ? "bg-[#FF6B2B] hover:bg-[#E65A20] text-white"
                    : "bg-[#1A3C6E] hover:bg-[#132D53] text-white"
                }`}
              >
                Choose {p.name}
              </Link>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
